import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search, Download } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet } from '../../lib/apiClient';

export const WaitlistTab = () => {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchQ, setSearchQ] = useState('');

  const fetchEntries = useCallback(async () => {
    try {
      const data = await apiGet('/api/admin/waitlist');
      setEntries(data.entries || []);
      setTotal(data.total || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const filtered = entries.filter(e =>
    e.email?.toLowerCase().includes(searchQ.toLowerCase()) ||
    e.company?.toLowerCase().includes(searchQ.toLowerCase())
  );

  const exportCSV = () => {
    const header = 'Email,Company,Status,Date\n';
    const rows = entries.map(e =>
      `${e.email},"${e.company || ''}",${e.status},${e.created_at}`
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `decoded_waitlist_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Waitlist exported');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-outfit text-lg font-semibold text-[#0B3D91]">
          Waitlist ({total} signups)
        </h3>
        <Button size="sm" variant="outline" onClick={exportCSV} data-testid="export-waitlist-btn">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search email or company..."
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          className="pl-9"
          data-testid="waitlist-search-input"
        />
      </div>
      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>
              <th className="text-left p-3 font-medium text-slate-500">#</th>
              <th className="text-left p-3 font-medium text-slate-500">Email</th>
              <th className="text-left p-3 font-medium text-slate-500">Company</th>
              <th className="text-left p-3 font-medium text-slate-500">Status</th>
              <th className="text-left p-3 font-medium text-slate-500">Date</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={5} className="p-6 text-center text-slate-400">No waitlist entries yet</td></tr>
            ) : (
              filtered.map((entry, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="p-3 text-slate-400">{i + 1}</td>
                  <td className="p-3 font-medium text-slate-900">{entry.email}</td>
                  <td className="p-3 text-slate-600">{entry.company || '-'}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      entry.status === 'waiting' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                    }`}>{entry.status}</span>
                  </td>
                  <td className="p-3 text-slate-500">{entry.created_at ? new Date(entry.created_at).toLocaleDateString() : '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
